import React, { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3 } from "lucide-react";
import StorageMeter from "@/components/StorageMeter";

const empty = { company_name: "", contact_name: "", email: "", phone: "", notes: "",
  subscription_package_id: null, subscription_end: "", grace_period_days: 7, status: "active",
  storage_quota_override_gb: null };

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(empty);
  const [storage, setStorage] = useState(null);
  const [overrideDraft, setOverrideDraft] = useState("");

  const load = async () => {
    try {
      const [c, p] = await Promise.all([
        api.get("/admin/clients"),
        api.get("/admin/packages"),
      ]);
      setClients(c.data);
      setPackages(p.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  // Fetch storage summary when editing an existing client
  useEffect(() => {
    if (!editing || editing === "new") { setStorage(null); setOverrideDraft(""); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/clients/${editing}/storage`);
        if (cancelled) return;
        setStorage(r.data);
        setOverrideDraft(r.data?.override_gb != null ? String(r.data.override_gb) : "");
      } catch (e) { if (!cancelled) toast.error(apiErrorMessage(e)); }
    })();
    return () => { cancelled = true; };
  }, [editing]);

  const saveOverride = async () => {
    const val = overrideDraft.trim() === "" ? null : Number(overrideDraft);
    if (val !== null && (Number.isNaN(val) || val < 0)) {
      toast.error("Override must be a non-negative number (GB)");
      return;
    }
    try {
      await api.put(`/admin/clients/${editing}/storage-override`, { storage_quota_override_gb: val });
      toast.success(val === null ? "Override cleared" : `Override set to ${val} GB`);
      const r = await api.get(`/admin/clients/${editing}/storage`);
      setStorage(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const save = async () => {
    try {
      const body = { ...form };
      if (!body.email) delete body.email;
      if (!body.subscription_end) body.subscription_end = null;
      if (editing === "new") {
        await api.post("/admin/clients", body);
        toast.success("Client created");
      } else {
        await api.put(`/admin/clients/${editing}`, body);
        toast.success("Client updated");
      }
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this client?")) return;
    try {
      await api.delete(`/admin/clients/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="px-8 py-8 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Studio</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Clients</h1>
        </div>
        <button
          onClick={() => { setEditing("new"); setForm(empty); }}
          data-testid="add-client-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700 transition"
        >
          <Plus className="h-4 w-4" /> New client
        </button>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3">Company</th>
              <th className="text-left px-5 py-3">Contact</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Subscription end</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {clients.length === 0 && (
              <tr><td colSpan="5" className="px-5 py-10 text-center text-zinc-500" data-testid="empty-clients">No clients yet.</td></tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="border-t border-zinc-100" data-testid={`client-row-${c.id}`}>
                <td className="px-5 py-4">
                  <div className="font-medium">{c.company_name}</div>
                  <div className="text-xs text-zinc-500">{c.email}</div>
                </td>
                <td className="px-5 py-4">{c.contact_name || "—"}</td>
                <td className="px-5 py-4">
                  <span className={`text-xs px-2 py-1 rounded ${c.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-700"}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-5 py-4 text-zinc-600">{c.subscription_end ? new Date(c.subscription_end).toLocaleDateString() : "—"}</td>
                <td className="px-5 py-4 text-right">
                  <button onClick={() => { setEditing(c.id); setForm({ ...empty, ...c }); }} data-testid={`edit-client-${c.id}`} className="p-1.5 hover:bg-zinc-100 rounded">
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button onClick={() => del(c.id)} data-testid={`del-client-${c.id}`} className="p-1.5 hover:bg-red-50 rounded text-red-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center md:justify-center" onClick={() => setEditing(null)}>
          <div className="bg-white w-full md:max-w-lg md:rounded-xl p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="client-form-modal">
            <div className="font-display text-xl mb-5">{editing === "new" ? "New client" : "Edit client"}</div>
            <div className="space-y-4">
              {[
                ["company_name", "Company name", "text", true],
                ["contact_name", "Contact name", "text"],
                ["email", "Email", "email"],
                ["phone", "Phone", "text"],
                ["subscription_end", "Subscription end (YYYY-MM-DD)", "text"],
                ["grace_period_days", "Grace period (days)", "number"],
              ].map(([k, label, type, required]) => (
                <div key={k}>
                  <label className="block text-xs font-medium text-zinc-700 mb-1.5">{label}{required && <span className="text-red-500">*</span>}</label>
                  <input
                    data-testid={`client-field-${k}`}
                    type={type}
                    value={form[k] ?? ""}
                    onChange={(e) => setForm({ ...form, [k]: type === "number" ? Number(e.target.value) : e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-zinc-300 bg-white outline-none focus:border-zinc-900"
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1.5">Status</label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 bg-white outline-none focus:border-zinc-900"
                  data-testid="client-field-status"
                >
                  <option value="active">Active</option>
                  <option value="grace">Grace</option>
                  <option value="expired">Expired</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-zinc-700 mb-1.5">Package</label>
                <select
                  value={form.subscription_package_id || ""}
                  onChange={(e) => setForm({ ...form, subscription_package_id: e.target.value || null })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 bg-white outline-none focus:border-zinc-900"
                  data-testid="client-field-package"
                >
                  <option value="">— None —</option>
                  {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            {editing !== "new" && storage && (
              <div className="mt-6 pt-5 border-t border-zinc-200">
                <StorageMeter data={storage} testid="admin-client-storage" />
                <div className="mt-4 flex items-end gap-2">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                      Admin override (GB) <span className="text-zinc-400 font-normal">— leave blank to use package limit</span>
                    </label>
                    <input
                      data-testid="admin-client-storage-override-input"
                      type="number"
                      step="0.1"
                      min="0"
                      value={overrideDraft}
                      onChange={(e) => setOverrideDraft(e.target.value)}
                      placeholder="e.g. 25"
                      className="w-full px-3 py-2 rounded-md border border-zinc-300 bg-white outline-none focus:border-zinc-900 text-sm"
                    />
                  </div>
                  <button
                    onClick={saveOverride}
                    data-testid="admin-client-storage-override-save"
                    className="px-3 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-zinc-300 text-sm" data-testid="client-cancel-btn">Cancel</button>
              <button onClick={save} className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm" data-testid="client-save-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
